import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  Button,
  TextField,
  Grid,
  Typography,
  IconButton,
  Box,
  MenuItem,
  Snackbar,
  Alert,
  CircularProgress,
  InputAdornment,
  useTheme,
  useMediaQuery,
  Slide,
  Checkbox,
  FormControlLabel,
  FormHelperText,
  Grow,
} from '@mui/material';
import {
  Close,
  Email,
  Business,
  Person,
  Phone,
  Category,
  Send,
  Notes,
  CheckCircle,
  Lock,
  RocketLaunch,
} from '@mui/icons-material';
import { useThemeMode } from '../contexts/ThemeContext';

// Slide-up transition
const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const INDUSTRIES = [
  'FinTech', 'Healthcare', 'Manufacturing', 'Retail',
  'Transportation', 'Education', 'Government',
  'Information Technology', 'Other',
];

function DemoRequestModal({ open, handleClose }) {
  const { isLight } = useThemeMode();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Palette shorthand
  const accent = isLight ? '#6366f1' : '#10b981';
  const accentGrad = isLight
    ? 'linear-gradient(135deg, #6366f1, #4f46e5)'
    : 'linear-gradient(135deg, #10b981, #0ea5e9)';

  // Form state
  const [formData, setFormData] = useState({
    firstName: '', lastName: '', email: '', company: '',
    phoneNumber: '', industry: '', message: '', consent: false,
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, severity: 'success', message: '' });

  // Shared input sx
  const fieldSx = {
    '& .MuiOutlinedInput-root': {
      background: isLight ? '#f8fafc' : 'rgba(15,23,42,0.6)',
      backdropFilter: 'blur(8px)',
      borderRadius: 2.5,
      '& fieldset': {
        borderColor: isLight ? '#e2e8f0' : 'rgba(255,255,255,0.1)',
      },
      '&:hover fieldset': { borderColor: `${accent}80` },
      '&.Mui-focused fieldset': { borderColor: accent, borderWidth: 2 },
    },
    '& .MuiInputLabel-root': {
      color: isLight ? 'rgba(15,23,42,0.6)' : 'rgba(248,250,252,0.6)',
      '&.Mui-focused': { color: accent },
    },
    '& .MuiInputBase-input': {
      color: isLight ? '#1e293b' : '#f1f5f9',
    },
  };

  // Handlers
  const handleChange = (e) => {
    const { name, value, checked, type } = e.target;
    setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: null }));
  };

  const validate = () => {
    const errs = {};
    if (!formData.firstName.trim()) errs.firstName = 'Required';
    if (!formData.lastName.trim()) errs.lastName = 'Required';
    if (!formData.email.trim()) errs.email = 'Required';
    else if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(formData.email)) errs.email = 'Invalid email';
    if (!formData.company.trim()) errs.company = 'Required';
    if (!formData.consent) errs.consent = 'Please agree to continue';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;

    setIsSubmitting(true);
    try {
      const res = await fetch('https://formspree.io/f/xdkdzdwl', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          firstName: formData.firstName, lastName: formData.lastName,
          email: formData.email, company: formData.company,
          phoneNumber: formData.phoneNumber || 'Not provided',
          industry: formData.industry || 'Not specified',
          message: formData.message || 'No message provided',
          submittedFrom: window.location.href,
          date: new Date().toString(),
        }),
      });
      if (!res.ok) throw new Error('Network error');
      setSuccess(true);
      setSnackbar({ open: true, severity: 'success', message: "Demo request submitted! We'll be in touch shortly." });
      setFormData({ firstName: '', lastName: '', email: '', company: '', phoneNumber: '', industry: '', message: '', consent: false });
    } catch {
      setSnackbar({ open: true, severity: 'error', message: 'Something went wrong. Please try again.' });
      setIsSubmitting(false);
    }
  };

  const handleCloseComplete = () => {
    setSuccess(false);
    setIsSubmitting(false);
    handleClose();
  };

  // Icon for adornment
  const adorn = (Icon) => (
    <InputAdornment position="start">
      <Icon sx={{ fontSize: '1.1rem', color: accent, opacity: 0.7 }} />
    </InputAdornment>
  );

  return (
    <>
      <Dialog
        open={open}
        onClose={isSubmitting ? undefined : handleClose}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
        TransitionComponent={Transition}
        slotProps={{
          backdrop: {
            sx: {
              backgroundColor: isLight ? 'rgba(15, 23, 42, 0.5)' : 'rgba(0, 0, 0, 0.65)',
              backdropFilter: 'blur(8px)',
              WebkitBackdropFilter: 'blur(8px)',
            },
          },
        }}
        PaperProps={{
          sx: {
            borderRadius: isMobile ? 0 : 4,
            overflow: 'hidden',
            background: isLight
              ? 'rgba(255, 255, 255, 0.92)'
              : 'rgba(10, 15, 30, 0.94)',
            backdropFilter: 'blur(32px)',
            WebkitBackdropFilter: 'blur(32px)',
            border: isLight
              ? '1px solid rgba(255,255,255,0.8)'
              : '1px solid rgba(255,255,255,0.08)',
            boxShadow: isLight
              ? '0 24px 64px rgba(99, 102, 241, 0.15), 0 1px 3px rgba(0,0,0,0.08)'
              : '0 24px 64px rgba(0,0,0,0.6)',
            maxWidth: isMobile ? undefined : 560,
            mx: 'auto',
          },
        }}
        aria-labelledby="demo-dialog-title"
      >
        {!success ? (
          <DialogContent sx={{ p: 0, overflow: 'auto' }}>
            {/* Header */}
            <Box sx={{ px: { xs: 3, sm: 4 }, pt: { xs: 3.5, sm: 4 }, pb: 2.5, position: 'relative' }}>
              {/* Close */}
              <IconButton
                onClick={handleClose}
                disabled={isSubmitting}
                aria-label="close"
                sx={{
                  position: 'absolute', top: 12, right: 12,
                  color: isLight ? 'rgba(15,23,42,0.4)' : 'rgba(248,250,252,0.4)',
                  '&:hover': { color: accent, background: `${accent}10` },
                }}
              >
                <Close sx={{ fontSize: '1.2rem' }} />
              </IconButton>

              {/* Icon + title */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Box
                  sx={{
                    width: 40, height: 40, borderRadius: 2.5,
                    background: `${accent}12`,
                    border: `1px solid ${accent}20`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}
                >
                  <RocketLaunch sx={{ fontSize: '1.2rem', color: accent }} />
                </Box>
                <Box>
                  <Typography
                    id="demo-dialog-title"
                    sx={{
                      fontWeight: 800,
                      fontSize: { xs: '1.25rem', sm: '1.4rem' },
                      color: isLight ? '#0f172a' : '#f8fafc',
                      lineHeight: 1.2,
                    }}
                  >
                    Request a Demo
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: '0.8rem',
                      color: isLight ? 'rgba(15,23,42,0.5)' : 'rgba(248,250,252,0.5)',
                      mt: 0.2,
                    }}
                  >
                    Get a personalized walkthrough
                  </Typography>
                </Box>
              </Box>
            </Box>

            {/* Form body */}
            <Box
              component="form"
              onSubmit={(e) => { e.preventDefault(); handleSubmit(); }}
              noValidate
              sx={{ px: { xs: 3, sm: 4 }, pb: { xs: 3, sm: 4 }, pt: 1 }}
            >
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <TextField
                    required fullWidth size="small"
                    name="firstName" label="First Name"
                    autoComplete="given-name"
                    InputProps={{ startAdornment: adorn(Person) }}
                    value={formData.firstName} onChange={handleChange}
                    error={!!errors.firstName} helperText={errors.firstName}
                    disabled={isSubmitting} sx={fieldSx}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    required fullWidth size="small"
                    name="lastName" label="Last Name"
                    autoComplete="family-name"
                    InputProps={{ startAdornment: adorn(Person) }}
                    value={formData.lastName} onChange={handleChange}
                    error={!!errors.lastName} helperText={errors.lastName}
                    disabled={isSubmitting} sx={fieldSx}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    required fullWidth size="small"
                    name="email" label="Business Email" type="email"
                    autoComplete="email"
                    InputProps={{ startAdornment: adorn(Email) }}
                    value={formData.email} onChange={handleChange}
                    error={!!errors.email} helperText={errors.email}
                    disabled={isSubmitting} sx={fieldSx}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    required fullWidth size="small"
                    name="company" label="Company Name"
                    autoComplete="organization"
                    InputProps={{ startAdornment: adorn(Business) }}
                    value={formData.company} onChange={handleChange}
                    error={!!errors.company} helperText={errors.company}
                    disabled={isSubmitting} sx={fieldSx}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth size="small"
                    name="phoneNumber" label="Phone"
                    autoComplete="tel"
                    InputProps={{ startAdornment: adorn(Phone) }}
                    value={formData.phoneNumber} onChange={handleChange}
                    disabled={isSubmitting} sx={fieldSx}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    select fullWidth size="small"
                    name="industry" label="Industry"
                    InputProps={{ startAdornment: adorn(Category) }}
                    SelectProps={{
                      MenuProps: {
                        PaperProps: {
                          sx: {
                            background: isLight ? 'rgba(255,255,255,0.92)' : 'rgba(15,23,42,0.92)',
                            backdropFilter: 'blur(20px)',
                            WebkitBackdropFilter: 'blur(20px)',
                            border: isLight ? '1px solid #e2e8f0' : '1px solid rgba(255,255,255,0.1)',
                            borderRadius: 2.5,
                            boxShadow: isLight
                              ? '0 12px 32px rgba(0,0,0,0.1)'
                              : '0 12px 32px rgba(0,0,0,0.4)',
                          },
                        },
                      },
                    }}
                    value={formData.industry} onChange={handleChange}
                    disabled={isSubmitting} sx={fieldSx}
                  >
                    <MenuItem value=""><em>Select</em></MenuItem>
                    {INDUSTRIES.map(ind => <MenuItem key={ind} value={ind}>{ind}</MenuItem>)}
                  </TextField>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth size="small"
                    name="message" label="How can we help?"
                    multiline rows={3}
                    placeholder="Share your integration needs or challenges..."
                    InputProps={{ startAdornment: (
                      <InputAdornment position="start" sx={{ alignSelf: 'flex-start', mt: 1.2 }}>
                        <Notes sx={{ fontSize: '1.1rem', color: accent, opacity: 0.7 }} />
                      </InputAdornment>
                    ) }}
                    value={formData.message} onChange={handleChange}
                    disabled={isSubmitting} sx={fieldSx}
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        name="consent" checked={formData.consent}
                        onChange={handleChange} disabled={isSubmitting}
                        size="small"
                        sx={{
                          color: isLight ? 'rgba(0,0,0,0.25)' : 'rgba(255,255,255,0.2)',
                          '&.Mui-checked': { color: accent },
                          p: 0.5,
                        }}
                      />
                    }
                    label={
                      <Typography sx={{ fontSize: '0.78rem', color: isLight ? '#475569' : '#94a3b8', lineHeight: 1.3, ml: 0.5 }}>
                        I agree to be contacted about Alphanetix products and services
                      </Typography>
                    }
                    sx={{ mx: 0, alignItems: 'center' }}
                  />
                  {errors.consent && (
                    <FormHelperText error sx={{ ml: 4 }}>{errors.consent}</FormHelperText>
                  )}
                </Grid>
              </Grid>

              {/* Submit */}
              <Button
                onClick={handleSubmit}
                variant="contained"
                fullWidth
                disabled={isSubmitting}
                endIcon={isSubmitting ? null : <Send sx={{ fontSize: '1rem' }} />}
                sx={{
                  borderRadius: 2.5, py: 1.3, mt: 1,
                  background: accentGrad,
                  boxShadow: `0 4px 20px ${accent}30`,
                  fontSize: '0.9rem', fontWeight: 700, textTransform: 'none',
                  '&:hover': {
                    transform: 'translateY(-1px)',
                    boxShadow: `0 8px 28px ${accent}40`,
                  },
                  '&:disabled': {
                    background: isLight ? '#e2e8f0' : '#334155',
                    color: isLight ? '#94a3b8' : '#64748b',
                  },
                }}
              >
                {isSubmitting ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CircularProgress size={16} color="inherit" />
                    Sending...
                  </Box>
                ) : 'Submit Request'}
              </Button>

              {/* Privacy note */}
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mt: 2.5, gap: 0.5 }}>
                <Lock sx={{ fontSize: '0.75rem', color: isLight ? 'rgba(0,0,0,0.2)' : 'rgba(255,255,255,0.15)' }} />
                <Typography sx={{ fontSize: '0.7rem', color: isLight ? 'rgba(0,0,0,0.3)' : 'rgba(255,255,255,0.25)' }}>
                  We never share your information with third parties
                </Typography>
              </Box>
            </Box>
          </DialogContent>
        ) : (
          /* Success view */
          <Box
            sx={{
              display: 'flex', flexDirection: 'column', alignItems: 'center',
              justifyContent: 'center', textAlign: 'center',
              py: { xs: 6, sm: 7 }, px: { xs: 3, sm: 5 },
            }}
          >
            <Grow in timeout={600}>
              <Box sx={{ maxWidth: 360 }}>
                <Box
                  sx={{
                    width: 72, height: 72, borderRadius: '50%',
                    background: accentGrad,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    mx: 'auto', mb: 3,
                    boxShadow: `0 12px 40px ${accent}30`,
                  }}
                >
                  <CheckCircle sx={{ color: '#fff', fontSize: '2.2rem' }} />
                </Box>

                <Typography sx={{ fontWeight: 800, fontSize: '1.5rem', color: isLight ? '#0f172a' : '#f8fafc', mb: 1 }}>
                  Request Submitted!
                </Typography>
                <Typography sx={{ fontSize: '0.9rem', color: isLight ? '#64748b' : '#94a3b8', lineHeight: 1.6, mb: 4 }}>
                  Our team will review your information and reach out within 24-48 hours to schedule your personalized demo.
                </Typography>

                <Button
                  variant="contained"
                  onClick={handleCloseComplete}
                  sx={{
                    borderRadius: 2.5, px: 5, py: 1.2,
                    background: accentGrad,
                    boxShadow: `0 4px 20px ${accent}30`,
                    fontSize: '0.9rem', fontWeight: 700, textTransform: 'none',
                    '&:hover': {
                      transform: 'translateY(-1px)',
                      boxShadow: `0 8px 28px ${accent}40`,
                    },
                  }}
                >
                  Done
                </Button>
              </Box>
            </Grow>
          </Box>
        )}
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar(s => ({ ...s, open: false }))}
          severity={snackbar.severity}
          variant="filled"
          sx={{
            borderRadius: 2.5,
            background: snackbar.severity === 'success' ? accentGrad : undefined,
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
}

export default DemoRequestModal;
